import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import Section from 'molecules/Section';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import { hasArrayValues } from 'utils/array/arrays';

import type { IconIds } from 'molecules/Icon/types';
import type { FC } from 'react';

const HeroAuthor: FC<HeroAuthorProps> = ({ name, title, bio, avatar, socials, hideGradient }) => {
  const avatarUrl = avatar?.file?.url || avatar?.url;

  return (
    <Section darkMode hasGradientTransition={!hideGradient}>
      {/*
        The design centres the headshot + copy as one group rather than
        stretching it across the container, so the row is centred and the copy
        keeps the 650px measure from the mockup.
      */}
      <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-start lg:justify-center lg:gap-24">
        {avatarUrl && (
          <ScrollFadeSection animateOnMount>
            <div className="size-20 shrink-0 overflow-hidden rounded-lg lg:size-[150px]">
              <Image
                url={avatarUrl}
                alt={`Headshot of ${name || 'the author'}`}
                className="size-full"
                objectCover
                noFill={true}
                width={300}
                height={300}
                sizes="(max-width: 1024px) 80px, 150px"
                priority
              />
            </div>
          </ScrollFadeSection>
        )}

        <div className="flex w-full flex-col gap-6 lg:w-[650px] lg:shrink-0 lg:gap-[30px]">
          <Heading heading={name} alignment="left" size="lg" as="h1" animateOnMount />

          {(hasArrayValues(socials) || title) && (
            <ScrollFadeSection delay={0.3} animateOnMount>
              <div className="flex flex-wrap items-center gap-5">
                {hasArrayValues(socials) &&
                  socials.map(socialLink => (
                    <Link
                      key={socialLink.icon}
                      href={socialLink.link}
                      className="text-white transition-colors hover:text-primary-300"
                      aria-label={`${name || 'Author'} on ${socialLink.icon}`}
                      enablePreview={false}
                    >
                      <Icon icon={socialLink.icon as IconIds} size={30} />
                    </Link>
                  ))}
                {title && <span className="text-lg text-gray-300">{title}</span>}
              </div>
            </ScrollFadeSection>
          )}

          {bio && (
            <ScrollFadeSection delay={0.4} animateOnMount>
              <p className="text-xl text-gray-300">{bio}</p>
            </ScrollFadeSection>
          )}
        </div>
      </div>
    </Section>
  );
};

export default HeroAuthor;
